/**
 * Cache-first-forever JSON fetch for genuinely immutable files — the v2
 * incremental contract's `events/<from>-<to>.json` chunks (`src/types
 * /incremental.ts`) and any other named artifact that never changes once
 * written (owner requirement 2026-09-26, requirement 2a: "cache-first for
 * ... every snapshot object addressed by digest"; a seq-range chunk is
 * addressed by its immutable range the same way an object is addressed by
 * its digest — once fetched, it is never re-fetched).
 *
 * Reuses the artifact IndexedDB store (`src/cache/idb.ts`) with `kind:
 * "json"` and the chunk's own URL as the cache key — chunks aren't
 * sha256-content-addressed today, but they are just as immutable, so the
 * same "never re-fetch, never revalidate" policy applies.
 */

import { idbGetArtifact, idbPutArtifact, isIndexedDbAvailable } from "./idb";
import { fetchBytes, fetchJson, SnapshotFetchError } from "../data/fetchJson";
import { verifySha256 } from "../data/hash";

/** `expectedSha256` (OQA): the published digest of the file's exact bytes (a frozen
 * chunk's `chunks[].sha256`). When given, the bytes are verified BEFORE they are
 * parsed, applied or cached — a mismatch (e.g. a same-named chunk of a newer episode
 * landing while an older index is still served) throws `ArtifactIntegrityError` and
 * nothing is stored under the old digest's key. */
export async function fetchImmutableJson<T>(url: string, expectedSha256?: string): Promise<T> {
  if (isIndexedDbAvailable()) {
    try {
      const cached = await idbGetArtifact(url);
      if (cached && cached.kind === "json") return cached.decoded as T;
    } catch {
      // Degrade to "no cache", never block the real fetch.
    }
  }
  const value =
    expectedSha256 === undefined
      ? await fetchJson<T>(url)
      : await fetchVerifiedJson<T>(url, expectedSha256);
  if (isIndexedDbAvailable()) {
    idbPutArtifact({ sha256: url, kind: "json", decoded: value, storedAt: Date.now() }).catch(
      () => {
        // Best-effort persistence only.
      },
    );
  }
  return value;
}

async function fetchVerifiedJson<T>(url: string, expectedSha256: string): Promise<T> {
  const bytes = await fetchBytes(url);
  try {
    await verifySha256(bytes, expectedSha256, url);
  } catch (error) {
    // The service worker cached these bytes cache-first before this check could
    // run (public/sw.js): evict them so the next poll goes to the network.
    await evictServiceWorkerEntry(url);
    throw error;
  }
  try {
    return JSON.parse(new TextDecoder("utf-8").decode(bytes)) as T;
  } catch (error) {
    throw new SnapshotFetchError(url, null, error);
  }
}

/** Must equal `DATA_CACHE` in public/sw.js (FIX3 split the old single cache; pinned by
 * src/sw/cachePolicy.test.ts). Snapshot objects are cached there, never in the asset cache. */
export const SERVICE_WORKER_CACHE = "observatory-data-v2";

async function evictServiceWorkerEntry(url: string): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(SERVICE_WORKER_CACHE);
    await cache.delete(new URL(url, globalThis.location?.href ?? "http://localhost/").href);
  } catch {
    // Best-effort: a missing Cache API (or a private-mode refusal) changes nothing.
  }
}
